import { useEffect, useRef, useState } from 'react';
import { ChatBubble, TypingBubble } from '@/shared/ChatBubble.jsx';
import { linkTarget, getDefaultDownload } from '@/content/site.js';

// Cycles the hero chat: each message types in (typing indicator), lands, then
// the next follows; after the last it clears and loops.
export default function Hero({ t }) {
  const sequence = t.sequence;
  const [visible, setVisible] = useState(0);
  const [typing, setTyping] = useState(false);
  const timers = useRef([]);
  // Resolve the CTA target from the visitor's OS: Windows → Google Drive build,
  // otherwise the macOS App Store.
  const defaultDownload = getDefaultDownload();

  // Keyed on the script, so switching language restarts the thread from the
  // first bubble rather than leaving half of it in the other language.
  useEffect(() => {
    setVisible(0);
    setTyping(false);
    const after = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); return id; };
    let i = 0;
    const tick = () => {
      setTyping(true);
      after(() => {
        setTyping(false);
        setVisible(i + 1);
        i += 1;
        if (i >= sequence.length) {
          after(() => { i = 0; setVisible(0); after(tick, 700); }, 3600);
        } else {
          after(tick, 1300);
        }
      }, 950);
    };
    after(tick, 500);
    return () => { timers.current.forEach(clearTimeout); timers.current = []; };
  }, [sequence]);

  return (
    <header id="top" className="hero">
      <div className="container">
        <div className="hero-grid">
          <div className="reveal">
            <div className="badge">
              <span className="badge-dot" />
              <span className="badge-text">{t.eyebrow}</span>
            </div>
            <h1 className="hero-h1">
              {t.titleLead}
              <span className="grad-text">{t.titleAccent}</span>
            </h1>
            <p className="hero-sub">{t.sub}</p>

            <div className="hero-ctas">
              <a href={defaultDownload.href} {...linkTarget(defaultDownload.href)} className="btn-primary">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff" aria-hidden="true">
                  <path d="M16.4 12.7c0-2 1.6-3 1.7-3-.9-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.2 2-1.4 2.4-.4 6 1 8 .7 1 1.4 2.1 2.4 2 1 0 1.3-.6 2.5-.6s1.5.6 2.5.6 1.7-1 2.3-2c.7-1.1 1-2.2 1-2.3 0 0-2-.8-2.1-3.1zM14.5 5.6c.5-.7.9-1.6.8-2.6-.8 0-1.7.5-2.3 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.8-.4 2.3-1z" />
                </svg>
                <span className="btn-label">{t.ctaDownload}</span>
              </a>
              <a href="#demo" className="btn-ghost">
                <span className="btn-ghost-play">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="#fff" aria-hidden="true"><path d="M6 4l14 8-14 8V4z" /></svg>
                </span>
                <span className="btn-label">{t.ctaDemo}</span>
              </a>
            </div>

            <div className="hero-platforms">
              <span>macOS</span><span className="sep">·</span>
              <span>Windows</span><span className="sep">·</span>
              <span>iOS</span><span className="sep">·</span>
              <span>Android</span>
            </div>
          </div>

          <div className="reveal chat-demo">
            <div className="chat-demo-glow" />
            <div className="chat-card">
              <div className="chat-head">
                <div className="chat-avatar">P</div>
                <div style={{ flex: 1 }}>
                  <div className="chat-name">{t.chatName}</div>
                  <div className="chat-status"><span className="on" /> {t.chatStatus}</div>
                </div>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="rgba(255,255,255,.45)" aria-hidden="true">
                  <path d="M12 1a5 5 0 00-5 5v4H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 9H9V6a3 3 0 016 0v4z" />
                </svg>
              </div>
              <div className="chat-body">
                {sequence.slice(0, visible).map((m, idx) => (
                  <ChatBubble key={idx} from={m.from} text={m.text} />
                ))}
                {typing && <TypingBubble variant="plain" />}
              </div>
              <div className="chat-input">
                <span className="ph">{t.chatPlaceholder}</span>
                <span className="send">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="#fff" aria-hidden="true"><path d="M3 11l18-8-8 18-2-7-8-3z" /></svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
